import { useMemo } from 'react';
import apiClient from 'src/api/axiosClient';
import { Subscription } from 'src/constants/types';
import { AdminCompany, FreeCreditsHistoryEntry } from '../types';
import {
  AssignmentTaskStatusResponse,
  CheckEligibilityResponse,
  CompanyBrandInfoInputBody,
  CompanyCampaignInfoInputBody,
  CompanyDocument,
  CompanyPhoneNumber,
  GetAdminCompanyDocumentsParams,
  GetAdminSubscriptionsParams,
  GetCompanyFreeCreditsHistoryParams,
  GetTcrCampaignApiOptions,
  GiveCompanyFreeCreditsParams,
  HostedNumberOrderResponse,
  LoaPreviewPayload,
  SignLoaPayload,
  SubscriptionPlanChangePreview,
  TcrCampaignStatusResponse,
  UpdateAdminCompaniesParams,
  UpdateCompanyOnboardingStatusParams,
  ValidateVerificationCodeResponse,
  VerificationMethod,
} from '../types/AdminCompaniesApi';
import { CompanyPhoneType } from './CompanyPhoneApi';

export function useAdminCompaniesApi() {
  return useMemo(
    () => ({
      // Companies
      getAdminCompany: (companyId: string) => {
        return apiClient.get<AdminCompany>(`/companies/${companyId}`);
      },

      updateAdminCompany: (params: UpdateAdminCompaniesParams, companyId: string) => {
        return apiClient.put<void>(`companies/${companyId}`, params);
      },

      updateCompanyOnboardingStatus: (
        companyId: string,
        params: UpdateCompanyOnboardingStatusParams,
      ) => {
        return apiClient.put<AdminCompany>(`companies/${companyId}/onboarding-status`, params);
      },

      giveCompanyFreeCredits: (companyId: string, params: GiveCompanyFreeCreditsParams) => {
        return apiClient.post<AdminCompany>(`companies/${companyId}/free-credits`, params);
      },

      getCompanyFreeCreditsHistory: (companyId: string, params: GetCompanyFreeCreditsHistoryParams) => {
        return apiClient.get<{ items: FreeCreditsHistoryEntry[]; count: number }>(
          `companies/${companyId}/free-credits/history`,
          { params },
        );
      },

      // Company Phone Numbers
      getAdminCompanyPhoneNumbers: (companyId: string) => {
        return apiClient.get<CompanyPhoneNumber[]>(`/companies/${companyId}/phone`);
      },

      createAdminCompanyPhoneNumber: (
        companyId: string,
        number: string,
        type: CompanyPhoneType,
      ) => {
        return apiClient.post<CompanyPhoneNumber>(`companies/${companyId}/phone/`, {
          number,
          type,
        });
      },

      updateAdminCompanyPhoneNumber: (
        companyId: string,
        phoneId: string,
        number: string,
        type: CompanyPhoneType,
      ) => {
        return apiClient.put<void>(`companies/${companyId}/phone/${phoneId}`, { number, type });
      },

      deleteAdminCompanyPhoneNumber: (companyId: string, phoneId: string) => {
        return apiClient.delete<void>(`companies/${companyId}/phone/${phoneId}`);
      },

      checkEligibility: (phoneNumber: string) => {
        return apiClient.post<CheckEligibilityResponse>(`/telnyx/check-eligibility`, {
          phoneNumber,
        });
      },

      createHostedNumberOrder: (companyId: string, phoneId: string) => {
        return apiClient.post<HostedNumberOrderResponse>(
          `companies/${companyId}/phone/${phoneId}/hosted-number-orders`,
        );
      },

      requestVerificationCode: (
        companyId: string,
        phoneId: string,
        orderId: string,
        verificationMethod: VerificationMethod,
      ) => {
        return apiClient.post<void>(
          `companies/${companyId}/phone/${phoneId}/request-verification-code`,
          {
            orderId,
            verificationMethod,
          },
        );
      },

      validateVerificationCode: (
        companyId: string,
        phoneId: string,
        orderId: string,
        code: string,
      ) => {
        return apiClient.post<ValidateVerificationCodeResponse>(
          `companies/${companyId}/phone/${phoneId}/validate-verification-code`,
          { orderId, code },
        );
      },

      attachDocumentsToOrder: (
        companyId: string,
        phoneId: string,
        orderId: string,
        loaDocumentId: string,
        billDocumentId: string,
      ) => {
        return apiClient.post<HostedNumberOrderResponse>(
          `companies/${companyId}/phone/${phoneId}/documents`,
          {
            orderId,
            loaDocumentId,
            billDocumentId,
          },
        );
      },

      getHostedNumberOrders: (companyId: string) => {
        return apiClient.get<HostedNumberOrderResponse[]>(
          `companies/${companyId}/hosted-number-orders`,
        );
      },

      deleteHostedNumberOrder: (companyId: string, orderId: string) => {
        return apiClient.delete<void>(`companies/${companyId}/hosted-number-orders/${orderId}`);
      },

      // Subscriptions
      getAdminSubscriptions: (params: GetAdminSubscriptionsParams) => {
        return apiClient.get<{ items: Subscription[]; count: number }>(`/subscriptions`, {
          params,
        });
      },

      createAdminCompanySubscription: (
        companyId: string,
        planId: string,
        startDate: string | undefined,
        autoRenew: boolean,
        startType: 'date' | 'onboarding_complete',
      ) => {
        return apiClient.post<Subscription>(`subscriptions`, {
          companyId,
          planId,
          ...(startDate !== '' && { startDate }),
          autoRenew,
          startType,
        });
      },

      updateAutoRenewStatusOfCompanySubscription: (subscriptionId: string, autoRenew: boolean) => {
        return apiClient.put<void>(`subscriptions/${subscriptionId}`, { autoRenew });
      },

      getSubscriptionPlanChangePreview: (subscriptionId: string, planId: string) => {
        return apiClient.get<SubscriptionPlanChangePreview>(
          `subscriptions/${subscriptionId}/plan-change-preview`,
          { params: { planId } },
        );
      },

      changeSubscriptionPlan: (subscriptionId: string, planId: string) => {
        return apiClient.put<Subscription>(`subscriptions/${subscriptionId}/plan`, { planId });
      },

      deleteAdminCompanySubscription: (subscriptionId: string) => {
        return apiClient.delete<void>(`subscriptions/${subscriptionId}`);
      },

      // Company Documents
      getAdminCompanyDocuments: (companyId: string, params: GetAdminCompanyDocumentsParams) => {
        return apiClient.get<{ items: CompanyDocument[]; count: number }>(
          `/companies/${companyId}/documents`,
          { params },
        );
      },

      attachDocumentToCompany: (companyId: string, formData: FormData) => {
        return apiClient.post<CompanyDocument>(`/companies/${companyId}/attach-document`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      },

      deleteCompanyDocument: (companyId: string, documentId: string) => {
        return apiClient.delete<void>(`/companies/${companyId}/documents/${documentId}`);
      },

      // Assignment Task Status
      getAssignmentTaskStatus: (companyId: string) => {
        return apiClient.get<AssignmentTaskStatusResponse>(
          `/companies/${companyId}/assignment-task-status`,
        );
      },

      assignTcrCampaign: (companyId: string) => {
        return apiClient.post<AssignmentTaskStatusResponse>(
          `/companies/${companyId}/assign-tcr-campaign`,
        );
      },

      getTcrCampaign: (tcrCampaignId: string, options?: GetTcrCampaignApiOptions) => {
        return apiClient.get<TcrCampaignStatusResponse>(`/telnyx/campaign/${tcrCampaignId}`, options);
      },

      reactivateTcrCampaign: (tcrCampaignId: string) => {
        return apiClient.post<TcrCampaignStatusResponse>(
          `/telnyx/campaign/${tcrCampaignId}/reactivate`,
        );
      },

      // LOA
      postAdminCompanyLoaPreview: (companyId: string, payload: LoaPreviewPayload) => {
        return apiClient.post<Blob>(`/companies/${companyId}/loa-preview`, payload, {
          responseType: 'blob',
        });
      },

      addAdminCompanyLoa: (companyId: string, payload: SignLoaPayload) => {
        return apiClient.post<CompanyDocument>(`/companies/${companyId}/loa-add`, payload);
      },

      saveCompanyBrandInfo: (companyId: string, payload: CompanyBrandInfoInputBody) => {
        return apiClient.put<void>(`/companies/${companyId}/brand-info`, payload);
      },

      saveCompanyCampaignInfo: (companyId: string, payload: CompanyCampaignInfoInputBody) => {
        return apiClient.put<void>(`/companies/${companyId}/campaign-info`, payload);
      },
    }),
    [],
  );
}

// Individual hooks kept for gradual migration of EditCompany tabs/dialogs.
export function useGetAdminCompanyApi() {
  return useAdminCompaniesApi().getAdminCompany;
}
export function useUpdateAdminCompanyApi() {
  return useAdminCompaniesApi().updateAdminCompany;
}
export function useUpdateCompanyOnboardingStatusApi() {
  return useAdminCompaniesApi().updateCompanyOnboardingStatus;
}
export function useGetAdminCompanyPhoneNumbersApi() {
  return useAdminCompaniesApi().getAdminCompanyPhoneNumbers;
}
export function useCreateAdminCompanyPhoneNumberApi() {
  return useAdminCompaniesApi().createAdminCompanyPhoneNumber;
}
export function useUpdateAdminCompanyPhoneNumberApi() {
  return useAdminCompaniesApi().updateAdminCompanyPhoneNumber;
}
export function useDeleteAdminCompanyPhoneNumberApi() {
  return useAdminCompaniesApi().deleteAdminCompanyPhoneNumber;
}
export function useCheckEligibilityApi() {
  return useAdminCompaniesApi().checkEligibility;
}
export function useCreateHostedNumberOrderApi() {
  return useAdminCompaniesApi().createHostedNumberOrder;
}
export function useRequestVerificationCodeApi() {
  return useAdminCompaniesApi().requestVerificationCode;
}
export function useValidateVerificationCodeApi() {
  return useAdminCompaniesApi().validateVerificationCode;
}
export function useAttachDocumentsToOrderApi() {
  return useAdminCompaniesApi().attachDocumentsToOrder;
}
export function useGetHostedNumberOrdersApi() {
  return useAdminCompaniesApi().getHostedNumberOrders;
}
export function useDeleteHostedNumberOrderApi() {
  return useAdminCompaniesApi().deleteHostedNumberOrder;
}
export function useGetAdminSubscriptionsApi() {
  return useAdminCompaniesApi().getAdminSubscriptions;
}
export function useCreateAdminCompanySubscriptionApi() {
  return useAdminCompaniesApi().createAdminCompanySubscription;
}
export function useUpdateAutoRenewStatusOfCompanySubscriptionApi() {
  return useAdminCompaniesApi().updateAutoRenewStatusOfCompanySubscription;
}
export function useGetSubscriptionPlanChangePreviewApi() {
  return useAdminCompaniesApi().getSubscriptionPlanChangePreview;
}
export function useChangeSubscriptionPlanApi() {
  return useAdminCompaniesApi().changeSubscriptionPlan;
}
export function useDeleteAdminCompanySubscriptionApi() {
  return useAdminCompaniesApi().deleteAdminCompanySubscription;
}
export function useGetAdminCompanyDocumentsApi() {
  return useAdminCompaniesApi().getAdminCompanyDocuments;
}
export function useAttachDocumentToCompanyApi() {
  return useAdminCompaniesApi().attachDocumentToCompany;
}
export function useDeleteCompanyDocumentApi() {
  return useAdminCompaniesApi().deleteCompanyDocument;
}
export function useGetAssignmentTaskStatusApi() {
  return useAdminCompaniesApi().getAssignmentTaskStatus;
}
export function useAssignTcrCampaignApi() {
  return useAdminCompaniesApi().assignTcrCampaign;
}
export function useGetTcrCampaignApi() {
  return useAdminCompaniesApi().getTcrCampaign;
}
export function useReactivateTcrCampaignApi() {
  return useAdminCompaniesApi().reactivateTcrCampaign;
}
export function usePostAdminCompanyLoaPreviewApi() {
  return useAdminCompaniesApi().postAdminCompanyLoaPreview;
}
export function useAddAdminCompanyLoaApi() {
  return useAdminCompaniesApi().addAdminCompanyLoa;
}
export function useSaveCompanyBrandInfoApi() {
  return useAdminCompaniesApi().saveCompanyBrandInfo;
}
export function useSaveCompanyCampaignInfoApi() {
  return useAdminCompaniesApi().saveCompanyCampaignInfo;
}
